import {
  Component,
  ViewChild,
  ElementRef,
  Input,
  Output,
  EventEmitter,
  ContentChildren,
  QueryList,
  TemplateRef,
  HostListener,
  OnInit,
  OnChanges,
  AfterContentInit,
  OnDestroy,
} from '@angular/core';
import { AppTemplateDirective } from 'app/directive';
import { HelperService, DataService } from 'app/services';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-multiselect',
  templateUrl: './multiselect.component.html',
  styleUrls: ['./multiselect.component.scss'],
})
export class MultiselectComponent
  implements OnInit, OnChanges, OnDestroy, AfterContentInit {
  @Input() placeholder: string = '';
  @Input() id: string = '';
  @Input() listId: string = '';
  @Input() values: any[] = [];
  @Input() options: any[];
  @Input() fontSize: number = 12;
  @Input() scrollHeight: string = '100px';
  @Input() listHeaderTitle: string = '';
  @Input() itemWidth: string = '';
  @Input() theme: string = ''; // [primary, light]
  @Input() styleClass: string = '';
  @Input() styleClassSelectedView: string = '';
  @Input() styleClassDropdownList: string = '';
  @Input() listItemFontSize: number = this.fontSize;
  @Input() disabled: boolean = false;
  @Input() field: string = 'name';
  @Input() headerTitle: string = '';
  @Input() hasHoverEffect: boolean = false;
  @Input() hasBgOnActive: boolean = false;
  @Input() showInitialsOnly: boolean = false;
  @Input() showFirstStringAndInitial: boolean = false;
  @Input() vPosition: string = 'bottom'; // ['top', 'bottom']
  @Input() hasCustomHeader: boolean = false;
  @Input() selectionLimit: number | undefined;

  @Output() appFocus = new EventEmitter<any>();
  @Output() appSelect = new EventEmitter<any>();
  @Output() appSave = new EventEmitter<any>();
  @Output() appClickOutside = new EventEmitter<any>();
  @Output() appOpenDropdown = new EventEmitter<any>();
  @Output() appCloseDropdown = new EventEmitter<any>();

  @ViewChild('multiselect', { static: true }) multiselect: ElementRef;
  @ViewChild('dropdownList', { static: true }) dropdownList: ElementRef;
  @ViewChild('multiDropdownInput', { static: true })
  multiDropdownInput: ElementRef;

  @ContentChildren(AppTemplateDirective) templates: QueryList<any>;

  multiSelectListItem: TemplateRef<any>;
  multiSelectHeader: TemplateRef<any>;
  isDropdownExpanded: boolean = false;
  selectedItemCount: number = 0;
  unsavedItemIndexes: number[] = [];
  dataServiceSub: Subscription = null;
  selectedOptionIndex: number = -1;

  constructor(
    public helperService: HelperService,
    public dataService: DataService
  ) {}

  ngOnInit() {
    this.dataServiceSub = this.dataService
      .getKeyPressEvent()
      .subscribe((event) => {
        if (event.key === 'Escape' || event.key === 'Tab') {
          this.close();
        }
      });
  }

  ngOnDestroy() {
    if (this.dataServiceSub) this.dataServiceSub.unsubscribe();
  }

  ngOnChanges(changes: any) {
    if (changes.values && changes.values.currentValue) {
      this.values = changes.values.currentValue;
      this.selectedItemCount = changes.values.currentValue.length;
    }
    if (changes.options && changes.options.currentValue) {
      this.recalculateSelectedItemCount();
    }
  }

  recalculateSelectedItemCount() {
    if (this.options && this.options.length) {
      this.selectedItemCount = this.options.filter(option => option.isChecked).length;
    } else {
      this.selectedItemCount = 0;
    }
  }

  ngAfterContentInit() {
    this.getCustomTemplate();
  }

  @HostListener('document:click', ['$event'])
  clickout(event) {
    /** Checking whether the click is outside the auto complete component or not (it will close the dropdown on click outside) */
    if (
      this.multiselect &&
      this.multiselect.nativeElement &&
      this.multiselect.nativeElement.contains(event.target)
    ) {
    } else {
      this.resetUnsavedChange();
      this.isDropdownExpanded = false;
      this.appClickOutside.emit();
    }
  }

  /** get all the custom templates */
  getCustomTemplate() {
    this.templates.forEach((item) => {
      switch (item.getType()) {
        case 'header':
          this.multiSelectHeader = item.template;
          break;
      }
    });
  }

  renderSelectedValue() {
    let selectedItem = '';
    this.values.forEach((value) => {
      if (value && value[this.field]) {
        if (this.showInitialsOnly) {
          selectedItem += `${this.helperService.fetchInitials(
            value[this.field]
          )}, `;
        } else if (this.showFirstStringAndInitial) {
          let firstName = '',
            lastName = '';
          if (value[this.field]) {
            [firstName, lastName] = value[this.field].split(' ');
            selectedItem += `${firstName} ${this.helperService.fetchInitials(
              lastName
            )}, `;
          }
        } else {
          selectedItem += `${value[this.field]}, `;
        }
      }
    });
    return selectedItem.slice(0, selectedItem.length - 2);
  }

  renderLabel(item) {
    return item[this.field];
  }

  onSelectOption(type: string) {
    if (type === 'up') {
      if (this.selectedOptionIndex === 0) {
        return;
      }

      this.selectedOptionIndex--;
    } else {
      if (this.selectedOptionIndex === this.options.length - 1) {
        this.selectedOptionIndex = 0;
      } else {
        this.selectedOptionIndex++;
      }
    }

    this.setFocusOnHighlightedOption();
  }

  setFocusOnHighlightedOption() {
    setTimeout(() => {
      const selectedEl = this.dropdownList.nativeElement.querySelector(
        `#item-${this.id || ''}${this.selectedOptionIndex}`
      ) as any;

      if (selectedEl) {
        const scrollHeight = parseInt(this.scrollHeight).toString();

        if (
          selectedEl.offsetTop >= scrollHeight ||
          selectedEl.offsetTop === 35
        ) {
          this.dropdownList.nativeElement.scrollTop =
            selectedEl.offsetTop -
            Number(scrollHeight) +
            selectedEl.offsetHeight;
        } else {
          this.dropdownList.nativeElement.scrollTop = 0;
        }
      }
    }, 10);
  }

  onEnter() {
    const option = this.options[this.selectedOptionIndex];

    this.onSelectItem(option, this.selectedOptionIndex);
  }

  openDropdown() {
    this.isDropdownExpanded = true;
    this.appOpenDropdown.emit();

    if (this.isDropdownExpanded) {
      setTimeout(() => {
        this.multiDropdownInput.nativeElement.focus();
      }, 10);
    }
  }

  onSelectItem(option: any, index: number) {
    if (this.selectionLimit != undefined && this.selectedItemCount >= this.selectionLimit && !option.isChecked) {
      this.helperService.showToaster(
        'error',
        `You can only select ${this.selectionLimit} technicians`
      );
      return;
    }
  
    if (option.isChecked) {
      const i = this.unsavedItemIndexes.indexOf(index);
      this.selectedItemCount -= 1;
      this.unsavedItemIndexes.splice(i, 1);
      option.isChecked = false;
    } else {
      this.unsavedItemIndexes.push(index);
      this.selectedItemCount += 1;
      option.isChecked = true;
    }
    this.onSave();
  }

  onSelectAll() {
    this.unsavedItemIndexes = [];
    this.options.forEach((option, index) => {
      if (
        index < this.selectionLimit && 
        this.selectedItemCount < this.selectionLimit || 
        this.selectionLimit == undefined 
      ) {
          this.unsavedItemIndexes.push(index);
          option.isChecked = true;
          this.selectedItemCount += 1;
      }
    });
    this.onSave();
  }

  resetUnsavedChange() {
    this.unsavedItemIndexes.forEach((i) => {
      this.options[i].isChecked = false;
    });

    this.unsavedItemIndexes = [];
  }

  onSave() {
    this.values = [];

    this.options.forEach((option) => {
      if (option.isChecked) {
        this.values.push(option);
      }
    });

    this.unsavedItemIndexes = [];
    this.appSave.emit(this.values);
  }

  onUnSelectAll() {
    this.selectedItemCount = 0;
    this.options.forEach((option) => {
      option.isChecked = false;
    });
    this.onSave();
  }

  close() {
    this.isDropdownExpanded = false;
  }

}
